<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { ArrowLeft, ArrowRight } from 'lucide-vue-next'
import { hasRole } from '@ck/auth'
import { customersApi, vehiclesApi, workOrdersApi } from '@ck/api-client'
import { formatDate } from '@ck/domain'
import { CkAlert, CkButton, CkStatTile, CkTable } from '@ck/ui'
import { apiErrorMessage, maskPii, totalPages } from '../lib/operational'

const props = defineProps<{ id: string }>()
const router = useRouter()
const canViewPii = computed(() => hasRole('admin') || hasRole('manager'))
const pageSize = 10
const vehiclePage = ref(1)
const workOrderPage = ref(1)
const customerId = computed(() => props.id)

const customerQuery = useQuery(computed(() => ({
  queryKey: ['customer', customerId.value],
  queryFn: () => customersApi.get(customerId.value),
  enabled: Boolean(customerId.value)
})))
const vehiclesQuery = useQuery(computed(() => ({
  queryKey: ['customer-vehicles', customerId.value, vehiclePage.value, pageSize],
  queryFn: () => vehiclesApi.list({ customerId: customerId.value, page: vehiclePage.value, pageSize }),
  enabled: Boolean(customerId.value)
})))
const workOrdersQuery = useQuery(computed(() => ({
  queryKey: ['customer-work-orders', customerId.value, workOrderPage.value, pageSize],
  queryFn: () => workOrdersApi.list({ customerId: customerId.value, page: workOrderPage.value, pageSize }),
  enabled: Boolean(customerId.value)
})))

watch(customerId, () => {
  vehiclePage.value = 1
  workOrderPage.value = 1
})

const customer = computed(() => customerQuery.data.value)
const vehicleResult = computed(() => vehiclesQuery.data.value)
const workOrderResult = computed(() => workOrdersQuery.data.value)
const vehiclePages = computed(() => totalPages(vehicleResult.value?.total ?? 0, pageSize))
const workOrderPages = computed(() => totalPages(workOrderResult.value?.total ?? 0, pageSize))
const customerErrorMessage = computed(() => {
  const status = (customerQuery.error.value as { problem?: { status?: number } } | null)?.problem?.status
  if (status === 401) return 'Sua sessão expirou. Entre novamente para visualizar o cliente.'
  if (status === 403) return 'Você não tem permissão para visualizar clientes no Operacional.'
  if (status === 404) return 'Cliente não encontrado. Volte à lista e tente abrir um cadastro atual.'
  return apiErrorMessage(customerQuery.error.value, 'Não foi possível carregar o cliente.')
})

function pii(value: string | null | undefined) {
  return canViewPii.value ? value || '—' : maskPii(value)
}

function openOrder(id: string) {
  router.push({ name: 'ordem-detalhe', params: { id } })
}

function statusLabel(status: string) {
  const labels: Record<string, string> = { open: 'Aberta', inProgress: 'Em andamento', completed: 'Concluída', cancelled: 'Cancelada' }
  return labels[status] ?? status
}
</script>

<template>
  <div class="page">
    <CkButton variant="ghost" class="back" @click="router.push({ name: 'clientes' })">
      <ArrowLeft :size="16" aria-hidden="true"/>Voltar para clientes
    </CkButton>

    <CkAlert v-if="customerQuery.isError.value" tone="error">{{ customerErrorMessage }}</CkAlert>
    <p v-else-if="customerQuery.isPending.value" class="status-message" role="status">Carregando cliente…</p>
    <template v-else-if="customer">
      <header class="customer-header">
        <div>
          <span class="ck-eyebrow">CLIENTE</span>
          <h1>{{ customer.name }}</h1>
          <p>{{ customer.email }}</p>
        </div>
        <span class="active-chip" :class="{ inactive: !customer.active }">{{ customer.active ? 'Ativo' : 'Inativo' }}</span>
      </header>

      <section class="summary" aria-label="Dados do cliente">
        <CkStatTile :value="vehicleResult?.total ?? '—'" label="Veículos" />
        <CkStatTile :value="workOrderResult?.total ?? '—'" label="Ordens de serviço" />
        <div class="data-tile"><span>Telefone</span><strong>{{ pii(customer.phone) }}</strong></div>
        <div class="data-tile"><span>CPF/CNPJ</span><strong class="ck-code">{{ pii(customer.cpfCnpj) }}</strong></div>
      </section>

      <section class="section">
        <div class="section-head"><div><span class="ck-eyebrow">GARAGEM</span><h2>Veículos do cliente</h2></div><span class="section-count">{{ vehicleResult?.total ?? 0 }}</span></div>
        <CkAlert v-if="vehiclesQuery.isError.value" tone="error">Não foi possível carregar os veículos.</CkAlert>
        <p v-else-if="vehiclesQuery.isPending.value" class="status-message" role="status">Carregando veículos…</p>
        <div v-else class="table-wrap">
          <CkTable
            :columns="[
              { key: 'vehicle', label: 'Veículo', width: '1.5fr' },
              { key: 'plate', label: 'Placa', width: '1fr' },
              { key: 'vin', label: 'VIN / chassi', width: '1.4fr' },
              { key: 'color', label: 'Cor', width: '1fr' }
            ]" :rows="vehicleResult?.items ?? []" :row-key="(row) => row.id" empty-message="Nenhum veículo cadastrado."
          >
            <template #default="{ item }">
              <span><strong>{{ item.brand }} {{ item.model }}</strong><small>{{ item.year }}</small></span>
              <span class="ck-code">{{ item.plate }}</span>
              <span class="ck-code pii-cell">{{ canViewPii ? item.vin || '—' : maskPii(item.vin) }}</span>
              <span>{{ item.color || '—' }}</span>
            </template>
          </CkTable>
        </div>
        <nav v-if="vehiclePages > 1" class="sub-pagination" aria-label="Paginação de veículos">
          <CkButton variant="ghost" :disabled="vehiclePage <= 1" aria-label="Veículos anteriores" @click="vehiclePage--"><ArrowLeft :size="15"/></CkButton>
          <span>{{ vehiclePage }} / {{ vehiclePages }}</span>
          <CkButton variant="ghost" :disabled="vehiclePage >= vehiclePages" aria-label="Próximos veículos" @click="vehiclePage++"><ArrowRight :size="15"/></CkButton>
        </nav>
      </section>

      <section class="section">
        <div class="section-head"><div><span class="ck-eyebrow">HISTÓRICO</span><h2>Ordens de serviço</h2></div><span class="section-count">{{ workOrderResult?.total ?? 0 }}</span></div>
        <CkAlert v-if="workOrdersQuery.isError.value" tone="error">Não foi possível carregar as ordens de serviço.</CkAlert>
        <p v-else-if="workOrdersQuery.isPending.value" class="status-message" role="status">Carregando ordens de serviço…</p>
        <div v-else class="table-wrap">
          <CkTable
            :columns="[
              { key: 'number', label: 'OS', width: '110px' },
              { key: 'vehicle', label: 'Veículo', width: '1.5fr' },
              { key: 'status', label: 'Status', width: '1fr' },
              { key: 'stage', label: 'Etapa', width: '1.3fr' },
              { key: 'delivery', label: 'Previsão', width: '120px' },
              { key: 'open', label: '', width: '60px', align: 'right' }
            ]" :rows="workOrderResult?.items ?? []" :row-key="(row) => row.id" empty-message="Nenhuma ordem de serviço para este cliente."
          >
            <template #default="{ item }">
              <button type="button" class="link-cell ck-code" @click="openOrder(item.id)">{{ item.number }}</button>
              <button type="button" class="link-cell" @click="openOrder(item.id)">{{ item.vehicle.brand }} {{ item.vehicle.model }}</button>
              <span><span class="status-chip">{{ statusLabel(item.status) }}</span></span>
              <span>{{ item.currentStage || '—' }}</span>
              <span class="ck-code">{{ formatDate(item.expectedDelivery) }}</span>
              <button type="button" class="open-cell" :aria-label="`Abrir OS ${item.number}`" @click="openOrder(item.id)"><ArrowRight :size="16"/></button>
            </template>
          </CkTable>
        </div>
        <nav v-if="workOrderPages > 1" class="sub-pagination" aria-label="Paginação de ordens de serviço">
          <CkButton variant="ghost" :disabled="workOrderPage <= 1" aria-label="OS anteriores" @click="workOrderPage--"><ArrowLeft :size="15"/></CkButton>
          <span>{{ workOrderPage }} / {{ workOrderPages }}</span>
          <CkButton variant="ghost" :disabled="workOrderPage >= workOrderPages" aria-label="Próximas OS" @click="workOrderPage++"><ArrowRight :size="15"/></CkButton>
        </nav>
      </section>
    </template>
  </div>
</template>

<style scoped>
.back { margin-bottom: var(--space-5); padding-inline: 0; }
.customer-header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); margin-bottom: var(--space-5); }
.ck-eyebrow { color: var(--ck-amber-400); }
h1 { margin: 5px 0 4px; font-size: var(--fs-2xl); font-weight: var(--fw-extrabold); }
.customer-header p { margin: 0; color: var(--color-text-muted); }
.active-chip, .status-chip { display: inline-flex; align-items: center; border-radius: var(--radius-full); padding: 4px 10px; background: var(--ck-ok-bg); color: var(--ck-ok-text); font-size: var(--fs-xs); font-weight: var(--fw-bold); }
.active-chip.inactive { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-4); margin-bottom: var(--space-8); }
.data-tile { display: flex; flex-direction: column; justify-content: center; min-height: 94px; padding: var(--space-4) var(--space-5); border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.data-tile span { color: var(--color-text-muted); font-size: var(--fs-xs); }
.data-tile strong { margin-top: var(--space-2); overflow-wrap: anywhere; font-size: var(--fs-sm); }
.section { margin-bottom: var(--space-8); }
.section-head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-4); margin-bottom: var(--space-3); }
.section-head h2 { margin: 4px 0 0; font-size: var(--fs-lg); }
.section-count { color: var(--color-text-faint); font-family: var(--font-mono); font-size: var(--fs-sm); }
.table-wrap { overflow-x: auto; }
.table-wrap :deep(.ck-table) { min-width: 700px; }
.table-wrap small { display: block; margin-top: 2px; color: var(--color-text-faint); }
.link-cell, .open-cell { border: 0; background: transparent; color: var(--color-text); font: inherit; text-align: left; cursor: pointer; }
.link-cell:hover, .open-cell:hover { color: var(--ck-amber-400); }
.open-cell { display: flex; justify-content: flex-end; width: 100%; color: var(--color-text-faint); }
.pii-cell { overflow-wrap: anywhere; }
.sub-pagination { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); margin-top: var(--space-3); color: var(--color-text-muted); font-size: var(--fs-xs); }
.sub-pagination :deep(.ck-button) { min-height: 34px; padding: 6px 9px; }
.status-message { padding: var(--space-6); color: var(--color-text-muted); }
@media (max-width: 950px) { .summary { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) {
  .customer-header { flex-direction: column; }
  .summary { grid-template-columns: 1fr 1fr; gap: var(--space-2); }
  .data-tile { min-height: 82px; padding: var(--space-3); }
}
</style>
